import { useCallback, useMemo, useRef, useState } from 'react';
import { isValid, parseISO } from 'date-fns';
import { Property, PropertyBusinessType } from 'models/Properties';
import useSearchURLParams, { URLParamsName } from 'hooks/useSearchURLParams';
import useAppUser from 'hooks/useAppUser';
import LegacyCalendarContext from 'pages/stackedCalendar/CalendarContext';
import CalendarContext from './CalendarContext';
import {
  CalendarFilter,
  JobsMap,
  LeadsMap,
  PropertiesMap,
  StackedCalendarMode,
} from './Calendar.types';
import {
  calendarModeLocalStorageKey,
  getCalendarFilterLocalStorageKey,
  stackedCalendarPropertiesConstants,
} from './Calendar.constants';

export const startDateOffsetByMode = {
  [StackedCalendarMode.BOOKING]: 45,
  [StackedCalendarMode.PRICING]: 30,
  [StackedCalendarMode.COMBINED]: 30,
};

function parseStoredDate(dateStr: string): Date | undefined {
  const parsedDate = parseISO(dateStr);
  return isValid(parsedDate) ? parsedDate : undefined;
}

// @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
const CalendarContextProvider = ({ children }) => {
  const { isOwner, userUid } = useAppUser();
  const storedFilter = JSON.parse(
    // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    localStorage.getItem(getCalendarFilterLocalStorageKey(userUid)),
  );

  const initialFilter = storedFilter
    ? {
        ...storedFilter,
        availableFrom: parseStoredDate(storedFilter.availableFrom),
        availableTo: parseStoredDate(storedFilter.availableTo),
        offset: 0,
        limit: stackedCalendarPropertiesConstants.defaultLimit,
      }
    : ({
        offset: 0,
        limit: stackedCalendarPropertiesConstants.defaultLimit,
        status: 'active',
      } as CalendarFilter);

  const [bodyWidth, setBodyWidth] = useState<number>(0);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [allProperties, setAllProperties] = useState<Property[]>([]);
  const [propertiesMap] = useState<PropertiesMap>({});
  const persistedMode = localStorage.getItem(calendarModeLocalStorageKey);

  const initialMode = isOwner
    ? StackedCalendarMode.COMBINED
    : persistedMode
    ? // @ts-expect-error TS7053 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      StackedCalendarMode[persistedMode]
    : StackedCalendarMode.BOOKING;

  const [mode, setMode] = useState<StackedCalendarMode>(initialMode);
  const [totalPropertiesNumber, setTotalPropertiesNumber] =
    useState<number>(-1);
  const [filter, setFilter] = useState<CalendarFilter>(initialFilter);
  const { getParameter } = useSearchURLParams();
  const selectedProperty = getParameter(URLParamsName.PROPERTY_UID);
  const [leadsMap] = useState<LeadsMap>({});
  const [jobsMap] = useState<JobsMap>({});
  const daysLeadsMap = useRef<
    Record<
      string,
      {
        uid: string;
        checkIn: Date;
        checkOut: Date;
      }[]
    >
  >({});

  const updatePropertiesMap = useCallback(
    ({ properties }: { properties: Property[] }) => {
      const propertiesToAdd = properties?.reduce(
        (propertiesAccumulator, property) => ({
          ...propertiesAccumulator,
          [property.uid]: property,
          ...(property.subUnits || []).reduce(
            (acc, subunit) => ({
              ...acc,
              [subunit.uid]: subunit,
            }),
            {},
          ),
          ...(property.unitTypes || []).reduce(
            (acc, unitType) => ({
              ...acc,
              [unitType.uid]: unitType,
              ...(unitType.units || []).reduce(
                (a, unit) => ({
                  ...a,
                  [unit.uid]: {
                    ...unit,
                    unitTypeUid: unitType.uid,
                    businessType: PropertyBusinessType.UNIT,
                  },
                }),
                {},
              ),
            }),
            {},
          ),
        }),
        {},
      );

      Object.assign(propertiesMap, propertiesToAdd);
    },
    [propertiesMap],
  );

  const calendarBodyWrapperRef = useRef<HTMLDivElement | null>(null);

  const contextValue = useMemo(
    () => ({
      allProperties,
      bodyWidth,
      filter,
      isFilterOpen,
      mode,
      propertiesMap,
      selectedProperty,
      totalPropertiesNumber,
      setBodyWidth,
      setFilter,
      setAllProperties,
      setIsFilterOpen,
      setMode,
      setTotalPropertiesNumber,
      updatePropertiesMap,
      leadsMap,
      jobsMap,
      daysLeadsMap,
      calendarBodyWrapperRef,
    }),
    [
      allProperties,
      bodyWidth,
      filter,
      isFilterOpen,
      mode,
      propertiesMap,
      selectedProperty,
      totalPropertiesNumber,
      leadsMap,
      jobsMap,
      daysLeadsMap,
    ],
  );

  return (
    // Also wrap in the master CalendarContext — pages/calendar/Calendar.tsx hard-codes that import.
    // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    <LegacyCalendarContext.Provider value={contextValue}>
      {/* @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified */}
      <CalendarContext.Provider value={contextValue}>
        {children}
      </CalendarContext.Provider>
    </LegacyCalendarContext.Provider>
  );
};

export default CalendarContextProvider;
